import { useCallback, useEffect } from 'react';
import { Form, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useClosingPackageCommands } from './useClosingPackageCommands';
import { useClosingPackageTaskModalData } from './useClosingPackageTaskModalData';
import {
  buildClientReceiptBody,
  getClientReceiptSubmitLabel,
  getClientReceiptSuccessMessage,
  validateClientReceiptSubmit,
} from '../lib/clientReceiptModalValidation';
import type { ClientReceiptResult, ClosingPackageOriginalsProblemType } from '../lib/originalsProblemTypes';
import { mapClosingPackageApiError } from '../lib/errorMapping';

export type ClientReceiptModalFormValues = {
  result: ClientReceiptResult;
  receivedAt: Dayjs;
  problemType?: ClosingPackageOriginalsProblemType;
  comment: string;
};

export type UseClientReceiptModalOptions = {
  applicationId: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

export function useClientReceiptModal({
  applicationId,
  open,
  onClose,
  onSuccess,
}: UseClientReceiptModalOptions) {
  const [form] = Form.useForm<ClientReceiptModalFormValues>();

  const { projection, contextView, contextLoading } = useClosingPackageTaskModalData({
    applicationId,
    open,
  });

  const { confirmOriginalsReceipt, states } = useClosingPackageCommands();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    form.setFieldsValue({
      result: 'RECEIVED',
      receivedAt: dayjs(),
      comment: '',
    });
  }, [form, open, applicationId]);

  const handleSubmit = useCallback(async () => {
    if (!projection) {
      message.error('Не удалось загрузить данные комплекта');
      return;
    }

    let values: ClientReceiptModalFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validationIssue = validateClientReceiptSubmit({
      result: values.result,
      eventAt: values.receivedAt,
      problemType: values.problemType ?? null,
      comment: values.comment,
    });

    if (validationIssue) {
      message.error(validationIssue.message);
      return;
    }

    try {
      await confirmOriginalsReceipt({
        applicationId,
        body: buildClientReceiptBody({
          result: values.result,
          comment: values.comment,
          receivedAt: values.receivedAt.toISOString(),
          problemType: values.result === 'PROBLEM' ? values.problemType ?? null : null,
        }),
      }).unwrap();

      message.success(getClientReceiptSuccessMessage(values.result));
      await onSuccess?.();
      onClose();
    } catch (error) {
      message.error(mapClosingPackageApiError(error));
    }
  }, [applicationId, confirmOriginalsReceipt, form, onClose, onSuccess, projection]);

  const receiptResult = Form.useWatch('result', form) ?? 'RECEIVED';

  return {
    form,
    projection,
    contextView,
    contextLoading,
    receiptResult,
    submitting: states.confirmOriginalsReceipt.isLoading,
    submitLabel: getClientReceiptSubmitLabel(receiptResult),
    handleSubmit,
  };
}
